/**
 * useTierUpgradeSync — auto-refresh the original tab to Pro after payment
 * completes in a second tab.
 *
 * Checkout opens in a new tab (see `stores/checkoutStore.ts`). When the user
 * pays there, this hook — mounted once for the authenticated app — brings the
 * ORIGINAL tab to the Pro state without a manual refresh.
 *
 * Design (CDO + Disruptor council): three layers, one reconcile primitive.
 *   1. `storage` event — the success tab writes `credia:tier-synced` AFTER it has
 *      itself confirmed PRO from /me, so by the time we react the webhook has
 *      already flipped the DB. Instant same-browser happy path.
 *   2. visibilitychange / focus — if an upgrade is pending and we're still FREE,
 *      reconcile when the user returns to this tab. Covers tab-closed-early,
 *      popup-blocked, and "just switched back". Throttled.
 *   3. cross-device poll — while pending and visible, poll a few times. The only
 *      path that flips a desktop tab when the user paid on their phone.
 *
 * All three funnel into one idempotent, in-flight-locked `reconcile()` that calls
 * the existing `checkAuth()` (GET /me is the sole source of truth). It only ever
 * moves FREE -> PRO and never trusts the cross-tab signal as state.
 */

import { useEffect, useRef } from 'react';
import { useAuthStore } from '../stores/authStore';
import {
  TIER_SYNC_KEYS,
  clearTierSync,
  isUpgradePending,
  isFreshSyncedValue,
} from '../lib/tierSync';

const RECONCILE_MAX_TRIES = 3;
const RECONCILE_GAP_MS = 2000;
const FOCUS_THROTTLE_MS = 10_000;
const POLL_INTERVAL_MS = 5000;
const POLL_MAX_MS = 2 * 60 * 1000;

/** Read the CURRENT store tier (non-hook — safe to call from listeners/timers). */
function isProNow(): boolean {
  const tier = useAuthStore.getState().user?.subscription_tier?.toLowerCase();
  return !!tier && tier !== 'free';
}

export function useTierUpgradeSync(): void {
  const reconcilingRef = useRef(false);
  const lastFocusReconcileRef = useRef(0);
  const pollStartRef = useRef<number | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    let cancelled = false;

    const stopPoll = () => {
      if (pollTimerRef.current != null) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      pollStartRef.current = null;
    };

    // Idempotent, in-flight-locked reconcile against server truth. Bounded burst
    // of checkAuth() absorbs residual webhook/read lag on the uncoordinated paths.
    const reconcile = async () => {
      if (reconcilingRef.current) {return;}
      if (isProNow()) {
        clearTierSync();
        stopPoll();
        return;
      }
      reconcilingRef.current = true;
      try {
        for (let i = 0; i < RECONCILE_MAX_TRIES; i++) {
          await useAuthStore.getState().checkAuth();
          if (cancelled) {return;}
          if (isProNow()) {
            clearTierSync();
            stopPoll();
            return;
          }
          if (i < RECONCILE_MAX_TRIES - 1) {
            await new Promise((resolve) => setTimeout(resolve, RECONCILE_GAP_MS));
            if (cancelled) {return;}
          }
        }
        // Still FREE — leave the UI as-is; focus/poll fallbacks will retry.
      } finally {
        reconcilingRef.current = false;
      }
    };

    // Cross-device poll: the only coverage when payment happened in another
    // browser/device and this tab never blurs. Bounded and self-expiring.
    const startPollIfNeeded = () => {
      if (pollTimerRef.current != null) {return;}
      if (!isUpgradePending() || isProNow()) {return;}
      if (typeof document !== 'undefined' && document.hidden) {return;}
      pollStartRef.current = Date.now();
      pollTimerRef.current = setInterval(() => {
        if (isProNow() || !isUpgradePending()) {
          stopPoll();
          return;
        }
        if (pollStartRef.current != null && Date.now() - pollStartRef.current > POLL_MAX_MS) {
          stopPoll();
          return;
        }
        if (typeof document !== 'undefined' && document.hidden) {
          // Pause while hidden; the focus handler resumes it.
          stopPoll();
          return;
        }
        void reconcile();
      }, POLL_INTERVAL_MS);
    };

    // Layer 1 — instant same-browser signal from the success tab.
    const onStorage = (e: StorageEvent) => {
      if (e.key === TIER_SYNC_KEYS.synced && isFreshSyncedValue(e.newValue)) {
        void reconcile();
      }
    };

    // Layer 2 — reconcile on return-to-tab while an upgrade is pending.
    const onVisibleOrFocus = () => {
      if (typeof document !== 'undefined' && document.hidden) {return;}
      if (!isUpgradePending() || isProNow()) {return;}
      const nowTs = Date.now();
      if (nowTs - lastFocusReconcileRef.current >= FOCUS_THROTTLE_MS) {
        lastFocusReconcileRef.current = nowTs;
        void reconcile();
      }
      startPollIfNeeded();
    };

    window.addEventListener('storage', onStorage);
    document.addEventListener('visibilitychange', onVisibleOrFocus);
    window.addEventListener('focus', onVisibleOrFocus);

    // Mounted mid-upgrade and visible (e.g. app reloaded on this tab after paying).
    if (isUpgradePending() && !isProNow()) {
      startPollIfNeeded();
    }

    return () => {
      cancelled = true;
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', onVisibleOrFocus);
      window.removeEventListener('focus', onVisibleOrFocus);
      stopPoll();
    };
  }, []);
}
